/**
 * Shared presentation helpers for the Fleet tab's TWO views (card + list).
 *
 * Extracted out of `FleetTable.tsx` (Task 8, Ruling B) so the card and the
 * list cannot drift on what "on target" looks like, what a short version
 * string is, or when a reinstall is blocked. Both `FleetDeviceCard.tsx` and
 * `FleetTable.tsx` import from here — neither defines its own copy.
 */
import React from 'react';
import type { FleetReleaseStatus, FleetRow } from './fleetRows';

/**
 * CAN THIS ROW BE SENT BACK TO BE BUILT AGAIN — and if not, why not.
 *
 * Mirrors the server's own guard (`requestReinstall`) rather than guessing at
 * it, so a card or row never offers an action that is going to be refused.
 * The reason is returned, not just a boolean, because "it has shipped" and
 * "it was never configured" need different things done about them and the
 * tooltip is where an operator finds that out.
 */
const REINSTALLABLE_STATES = new Set(['provisioning', 'configured', 'packing']);
export function reinstallBlock(d: FleetRow): string | undefined {
  if (!d.unitId) return 'This row is a check-in with no hardware unit joined to it yet.';
  if (d.reinstallRequestedAt) return 'A reinstall is already requested — it is waiting in Pending Configuration.';
  if (!d.unitState) return undefined;
  if (!REINSTALLABLE_STATES.has(d.unitState)) {
    return d.unitState === 'in_stock' || d.unitState === 'allocated'
      ? `This device is "${d.unitState}" — it has not been configured yet, so install it rather than reinstalling.`
      : `This device is "${d.unitState}" — it has left us, and the RubyTill install only runs over local adb. `
        + 'Record it as returned or rma when it comes back, then reinstall it.';
  }
  return undefined;
}

export function installCell(d: FleetRow) {
  if (d.bootInstalled === true) return <span className="text-emerald-600 font-extrabold">✓ installed</span>;
  if (d.error) return <span className="text-red-500 font-extrabold">✗ {d.error}</span>;
  return <span className="text-gray-400 font-extrabold">• pending</span>;
}

/**
 * A 40-character git SHA is what a LOCAL bench build stamps as its version
 * (`install-device.sh` bakes `git rev-parse HEAD` into VITE_APP_VERSION), and
 * it is unreadable in a table or a card. Shorten it for display ONLY — the
 * full value stays in the title attribute, because it is the string OTA
 * compares and the one an engineer pastes into `git show`.
 *
 * A published dev/prod build carries a real version string instead, which is
 * already short and is left exactly as it is.
 */
const LOOKS_LIKE_SHA = /^[0-9a-f]{16,}(-dirty)?$/i;
export const shortVersion = (v: string): string =>
  LOOKS_LIKE_SHA.test(v) ? `${v.slice(0, 7)}${v.toLowerCase().endsWith('-dirty') ? '-dirty' : ''}` : v;

/**
 * Install stamps are absolute dates — "3 days ago" is useless in an audit.
 * The cell shows the DATE; the exact time and who ran it live in the tooltip,
 * because a column (or a card row) that wraps onto three lines stops being
 * read at all.
 */
export const installedOn = (at: number): string =>
  new Date(at).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });

export const installedAtFull = (at: number, by?: string): string =>
  `Installed ${new Date(at).toLocaleString()}${by ? ` by ${by}` : ''}`;

/**
 * "4 min ago" / "2 days ago" — the card and the list view both use this for
 * "last check-in", so a three-day-old check-in reads the same age in both
 * (the list previously used a bare clock time, which read as "today" for a
 * check-in that was anything but).
 */
export function checkInAgo(at: number, now = Date.now()): string {
  const ms = Math.max(0, now - at);
  const min = Math.round(ms / 60000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min} min ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr} hr${hr === 1 ? '' : 's'} ago`;
  const day = Math.round(hr / 24);
  return `${day} day${day === 1 ? '' : 's'} ago`;
}

/** A device is treated as stale-seen (amber, "2 days ago") past this age. */
export const STALE_SEEN_MS = 24 * 60 * 60 * 1000;

export const STATUS_STYLE: Record<FleetReleaseStatus, { label: string; cls: string }> = {
  'on-target': { label: 'on target', cls: 'bg-emerald-500/15 text-emerald-600' },
  pinned: { label: 'pinned', cls: 'bg-rose-500/15 text-rose-600' },
  behind: { label: 'behind', cls: 'bg-amber-500/15 text-amber-600' },
  stale: { label: 'stale', cls: 'bg-gray-400/20 text-gray-500' },
  unknown: { label: 'unknown', cls: 'bg-gray-400/15 text-gray-400' },
};

/**
 * How the device's target was DECIDED. A pinned device is being held by an
 * operator; a ring-following device moves with its ring on the next promote.
 * `null` is a target whose source the server did not state: shown as such,
 * never guessed from the pin.
 */
export const TARGET_SOURCE_STYLE: Record<'pin' | 'ring' | 'unknown', { label: string; cls: string }> = {
  // Wording is deliberately NOT the bare word "pinned" — that is already the
  // release-status pill, and two pills reading the same word in one row is
  // how a reader stops distinguishing them.
  pin: { label: 'held by pin', cls: 'bg-rose-500/15 text-rose-600' },
  ring: { label: 'follows ring', cls: 'bg-emerald-500/15 text-emerald-600' },
  unknown: { label: 'source unknown', cls: 'bg-gray-400/15 text-gray-400' },
};

export const RING_STYLE: Record<string, string> = {
  canary: 'bg-amber-500/15 text-amber-600',
  pilot: 'bg-sky-500/15 text-sky-600',
  stable: 'bg-emerald-500/15 text-emerald-600',
};

/** Where a build came from. `local` is a bench build off somebody's working tree. */
export const SOURCE_STYLE: Record<string, string> = {
  local: 'bg-amber-500/15 text-amber-600',
  dev: 'bg-sky-500/15 text-sky-600',
  prod: 'bg-emerald-500/15 text-emerald-600',
};
